/**
 * AccountFilterRenderer
 *
 * Renders a PlanStatementRow's `accountFilterJson` as the condition editor instead of a raw JSON text
 * field. An account-set row selects an account group subtree AND this filter
 * (PlanRollup-Statement-Templates §5); the filter is stored as a `ConditionDto` JSON string and
 * compiled server-side by `AccountSetResolver`.
 *
 * A text box was the wrong surface twice over: the column is `jsonb`, so anything that is not JSON was
 * rejected by Postgres as a bare "An internal error occurred", and anything that was JSON but not a
 * condition was silently ignored at read, leaving a row that quietly selected the whole subtree. The
 * editor can only produce what the resolver evaluates, so neither is reachable from here.
 *
 * The field list comes from the template's own endpoint rather than the plan-entry one that serves the
 * identical list for an ACCOUNT row axis: authoring a report template is not entering a plan, so it
 * must not require plan-entry permission. Both are the declaration `AccountSetResolver` filters with.
 */

import { useMemo } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';
import {
  buildRowAxisContextSchema,
  resolveFilterFieldLabel,
  type RowAxisFilterFieldDto,
} from '@/finance/rowAxisFilterSchema';
import { ConditionJsonControl } from './ConditionJsonControl';

/** The account fields a statement row may narrow its account group subtree on. */
const ACCOUNT_FILTER_FIELDS_URL = '/api/finance/planstatementtemplate/account-filter-fields';

function scopeProperty(uischema: unknown): string | undefined {
  const scope = (uischema as { scope?: string })?.scope;
  return scope ? scope.split('/').pop() : undefined;
}

const AccountFilterRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, errors, visible, label } = props;
  const { t } = useTranslation();

  const { data: fields } = useQuery({
    queryKey: ['planStatementAccountFilterFields'],
    queryFn: async () => (await apiClient.get<RowAxisFilterFieldDto[]>(ACCOUNT_FILTER_FIELDS_URL)).data,
    // A row type that cannot carry an account filter should not fetch the field list either.
    enabled: visible !== false,
    staleTime: CACHE_TIME.METADATA,
  });

  // The owning module authors its field labels in English (they come from the DSL), so translate them
  // the way the rollup shaping filter does. The group prefix it composes is dropped: every field here
  // belongs to the account, and the control already says so.
  const localizedFields = useMemo(
    () => (fields ?? []).map((f) => ({ ...f, label: resolveFilterFieldLabel(t, { ...f, groupLabel: undefined }) })),
    [fields, t],
  );

  // null while loading, so the editor shows its own loading state rather than an empty field list.
  const contextSchema = useMemo(
    () => (fields ? buildRowAxisContextSchema(localizedFields, 'Account') : null),
    [fields, localizedFields],
  );

  return (
    <ConditionJsonControl
      value={data}
      onChange={(json) => handleChange(path, json)}
      contextSchema={contextSchema}
      error={errors}
      visible={visible}
      label={label}
    />
  );
};

export const AccountFilterRenderer = withJsonFormsControlProps(AccountFilterRendererComponent);

/**
 * Tester — the `accountFilterJson` control of a PlanStatementRow. Rank 20, above the text renderers.
 * The root-schema guard mirrors `rowAxisFilterTester`: match the row this filter belongs to, not any
 * field that happens to share the name.
 */
export const accountFilterTester = rankWith(20, (uischema, _schema, context) => {
  if (!isControl(uischema)) return false;
  if (scopeProperty(uischema) !== 'accountFilterJson') return false;
  const rootProps = (context?.rootSchema as { properties?: Record<string, { enumType?: string }> })?.properties;
  return rootProps?.rowType?.enumType === 'StatementRowType' && !!rootProps?.accountGroupRootRef;
});

export const accountFilterEntry = {
  tester: accountFilterTester,
  renderer: AccountFilterRenderer,
};
